import { Alert } from '@design-system/alert';
import { Button } from '@design-system/button';
import { PageBlock } from '@design-system/page-block';
import React, { FC, useContext, useState } from 'react';

import { LineItem, StoreContext } from '~/context/shopify';

import CartState from './CartState';
import { EmptyStateProps } from './EmptyState/models';
import Headline from './Headline';
import { ShoppingCartProps } from './models';
import ProductsList from './ProductsList';
import SummaryAndPayment from './SummaryAndPayment';

const ShoppingCart: FC<ShoppingCartProps> = ({
  pageBlockUI,
  componentId,
  continueShoppingButton,
  productCartItemSettings,
  alertSettings: { alertMessage, icon },
  alertButtonSettings: { confirmButton, declineButton },
  paymentMethods,
  paymentMethodsLabel,
  paymentMethodsLabelUI,
  paymentDescriptionsWithImage,
  cartHeadline,
  cartSummary,
  emptyState,
  uid,
  cartItems,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const [idToDelete, setIdToDelete] = useState(0);
  const { checkout, removeLineItem, isCartFetching } = useContext(StoreContext);

  const handleRemoveItem = (id: number) => {
    setIdToDelete(id);
    setShowAlert(true);
  };

  const removeItem = () => {
    removeLineItem(checkout.id, idToDelete);
    setShowAlert(false);
  };

  const handleCheckout = () => window.open(checkout.webUrl);

  const isCartEmpty = !cartItems?.length;
  const cartStates = {
    isCartEmpty,
    isCartFetching,
    emptyStateSettings: {
      ...emptyState,
      continueShoppingButton,
    } as EmptyStateProps,
  };

  return (
    <PageBlock
      {...pageBlockUI}
      id={componentId}
      className="shopping-cart"
      componentUid={uid}
    >
      {isCartEmpty || isCartFetching ? (
        <CartState {...cartStates} />
      ) : (
        <>
          <Headline
            {...cartHeadline}
            productsAmount={checkout.lineItems.length}
          />
          <div className="shopping-cart__content">
            <ProductsList
              {...{
                productCartItemSettings,
                continueShoppingButton,
                handleRemoveItem,
              }}
              products={cartItems as LineItem[]}
            />
            <SummaryAndPayment
              {...{
                cartSummary,
                paymentMethods,
                paymentMethodsLabel,
                paymentMethodsLabelUI,
                paymentDescriptionsWithImage,
                handleCheckout,
              }}
            />
          </div>
        </>
      )}
      <Alert
        icon={icon[0].icon}
        showAlert={showAlert}
        alertMessage={alertMessage}
        action={
          <>
            <Button onClick={removeItem} {...confirmButton}>
              {confirmButton.children}
            </Button>
            <Button onClick={() => setShowAlert(false)} {...declineButton}>
              {declineButton.children}
            </Button>
          </>
        }
      />
    </PageBlock>
  );
};

export default ShoppingCart;
